import type {ReactElement} from 'react';
import React from 'react';
import {PosterEditingBaseColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-base-color-panel';
import type {ColorStorage} from '@Components/color-picker-v2';
import type {ColorFillOption} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {COLOR_OBJECT_IDS} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromFancyTextItem} from '@Hooks/poster-editor/useGetKeyFromFancyTextItem';
import type {RGBA} from '@Utils/color.util';
import {getRGBAArrayFromColorStorage} from '@Utils/color.util';

interface FancyTextColorPanelProps {
  id: ColorFillOption;
}

export function FancyTextColorPanel({...props}: FancyTextColorPanelProps): ReactElement<any> | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeItemColors = useGetKeyFromFancyTextItem('colors');

  if (!activeItem) {
    throw new Error('Active item does not exist');
  }

  if (!activeItem.isFancyText() || !activeItemColors) {
    throw new Error(`Expected Active item to be of type fancy text, instead got ${activeItem.gitype}`);
  }

  const isColorOptionPresent = (): boolean => {
    const totalShadesPresent = activeItem.colors.length;
    const index = getIndexOfColorChanging() + 1;
    return totalShadesPresent - index >= 0;
  };

  const getIndexOfColorChanging = (): number => {
    const indexOfObject = COLOR_OBJECT_IDS.indexOf(props.id);
    if (indexOfObject === -1) {
      throw new Error('Id mismatch for color object of fancy text');
    }
    return indexOfObject;
  };

  if (!isColorOptionPresent()) {
    return null;
  }

  const getColorObject = (): ColorStorage[] => {
    const indexOfObject = getIndexOfColorChanging();
    return [
      {
        id: props.id,
        color: activeItemColors[indexOfObject],
      },
    ];
  };

  const prepareNewColorArray = (updatedColor: RGBA): RGBA[] => {
    const newColorArray: RGBA[] = [];
    for (let index = 0; index < activeItemColors.length; index += 1) {
      if (index === getIndexOfColorChanging()) {
        newColorArray.push(updatedColor);
      } else {
        newColorArray.push(activeItemColors[index]);
      }
    }
    return newColorArray;
  };

  const onChangeColor = (updatedColor: ColorStorage[]): void => {
    const updatedRGBAArray = getRGBAArrayFromColorStorage(updatedColor)[0];
    const newColorArray = prepareNewColorArray(updatedRGBAArray);

    void activeItem.updateFromObject(
      {
        colors: newColorArray,
      },
      {
        doInvalidate: false,
      }
    );
  };
  const onChangeColorEnded = (updatedColor: ColorStorage[]): void => {
    const updatedRGBAArray = getRGBAArrayFromColorStorage(updatedColor)[0];
    const newColorArray = prepareNewColorArray(updatedRGBAArray);

    void activeItem.updateFromObject(
      {
        colors: newColorArray,
      },
      {
        doInvalidate: true,
      }
    );
  };

  return (
    <PosterEditingBaseColorPanel
      id="fancy-text-color-panel"
      addDelayToOnChangeColor
      isGradientEnabled={false}
      color={getColorObject()}
      onChangeColor={onChangeColor}
      onChangeEnded={onChangeColorEnded}
      isAlphaDisabled
    />
  );
}
